import React, { type ComponentType, useLayoutEffect, useRef } from "react"
import { useDispatch, useSelector, useStore } from "react-redux"
import { useNavigate } from "react-router-dom"

import { pairAdopted } from "../containers/LoginPage/redux/auth.slice"
import {
  logout,
  refreshToken
} from "../containers/LoginPage/redux/auth.thunks.ts"
import { errorSave } from "../redux/errors.slice"
import { reportTransportFailure } from "../observability/transport-failures"
import services, { Api } from "../services/api"
import { PERSIST_KEY, newerPairFromAnotherTab } from "../services/token-refresh"
import type { AppDispatch, RootState } from "../store"
import {
  createAuthRequestInterceptor,
  createAuthResponseErrorHandler
} from "./auth-interceptors"

function withAuth<P extends object>(WrappedComponent: ComponentType<P>) {
  function WithAuthComponent(props: P) {
    const api = new Api()
    const dispatch = useDispatch<AppDispatch>()
    const navigate = useNavigate()
    const store = useStore<RootState>()

    const auth = useSelector((state: RootState) => state.auth)

    const isLoggingOutRef = useRef(false)

    useLayoutEffect(() => {
      // Reset flag only on successful login (when accessToken appears)
      // Don't reset during logout (when accessToken disappears)
      if (auth?.accessToken) {
        isLoggingOutRef.current = false
      }

      const requestInterceptor = api.interceptors.request.use(
        createAuthRequestInterceptor({
          getAccessToken: () => store.getState().auth?.accessToken ?? null
        })
      )

      const responseInterceptor = api.interceptors.response.use(
        (res) => res,
        createAuthResponseErrorHandler({
          refresh: () => dispatch(refreshToken({ navigate })).unwrap(),
          retry: (config) => api.api.axiosInstance.request(config as any),
          logout: () => {
            if (!isLoggingOutRef.current) {
              isLoggingOutRef.current = true
              dispatch(logout({ navigate }))
            }
          },
          reportTransportFailure,
          saveNetworkError: (err) => dispatch(errorSave(err))
        })
      )

      const adoptFromAnotherTab = (event: StorageEvent) => {
        if (event.key !== null && event.key !== PERSIST_KEY) {
          return
        }
        const pair = newerPairFromAnotherTab(
          store.getState().auth?.accessToken ?? null
        )
        if (pair) {
          services.updateAccessToken(pair.access, pair.refresh)
          dispatch(pairAdopted(pair))
        }
      }
      window.addEventListener("storage", adoptFromAnotherTab)

      // Cleanup interceptors on unmount
      return () => {
        window.removeEventListener("storage", adoptFromAnotherTab)
        api.interceptors.request.eject(requestInterceptor)
        api.interceptors.response.eject(responseInterceptor)
      }
    }, [auth?.accessToken, auth?.refreshToken, dispatch, navigate])

    return <WrappedComponent {...props} />
  }

  // Preserve display name for debugging
  const wrappedName =
    WrappedComponent.displayName || WrappedComponent.name || "Component"
  WithAuthComponent.displayName = `withAuth(${wrappedName})`

  return WithAuthComponent
}

export default withAuth
